import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { fadeInOutAnimation } from '@app/core/animations';
import { ASSETS_DIMENSIONS } from '@app/core/constants/constants';
import { SetCurrentApp } from '@app/core/states/app.actions';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { NextStep, SkipToStep } from '@app/core/states/stepper.actions';
import { StepperComponent } from '@app/core/stepper/stepper.component';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import {
  CropperState,
  ImageCropperComponent,
} from '@app/ui/components/image-cropper/image-cropper.component';
import { SelectedImage } from '@app/ui/components/image-upload/image-upload.component';
import { UIState } from '@app/ui/states/ui.state';
import { TranslateModule } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { combineLatest, filter, map, Observable, Subscription } from 'rxjs';
import { StepperState } from '@app/core/states/stepper.state';
import { LogoAssetsSectionComponent } from '../components/logo-assets-section/logo-assets-section.component';
import { LogoUploadComponent } from '../components/logo-upload/logo-upload.component';
import { SplashscreenAssetsSectionComponent } from '../components/splashscreen-assets-section/splashscreen-assets-section.component';
import { SplashscreenUploadComponent } from '../components/splashscreen-upload/splashscreen-upload.component';
import { ASSET_MAIN_CONTAINER_GAP, AssetUiService } from '../services/assets-ui-service';
import { ImageResizingService, ImageSource } from '../services/image-resizing.service';
import { FinishCreationFlowService } from '../services/finish-creation-flow.service';
import {
  FetchAssetsRequest,
  PostImageAssetsRequest,
  SaveCropperState,
  SetRawImage,
  StoreAsset,
} from '../states/assets.actions';
import { AssetKey, AssetsState, constructAssetUrl } from '../states/assets.state';

@Component({
  selector: 'rk-app-settings-creation',
  imports: [
    TranslateModule,
    AsyncPipe,
    LogoUploadComponent,
    LogoAssetsSectionComponent,
    SplashscreenUploadComponent,
    SplashscreenAssetsSectionComponent,
    StepperComponent,
  ],
  templateUrl: './app-settings-creation.component.html',
  styleUrl: './app-settings-creation.component.scss',
  animations: [fadeInOutAnimation],
})
export class AppSettingsCreationComponent implements OnInit, OnDestroy {
  @Select(AppConfigurationState.isPaidApp)
  isPaidApp$: Observable<boolean>;
  @Select(AssetsState.fetchedAssets)
  fetchedAssets$: Observable<{ [key: string]: string }>;
  @Select(AppConfigurationState.currentAppIdPlan)
  planId$: Observable<number>;
  @Select(AssetsState.isLoading)
  isLoading$: Observable<boolean>;
  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;
  @Select(AppConfigurationState.availableOnAndroidTV)
  availableOnAndroidTV$: Observable<boolean>;
  @Select(UIState.menuWidth)
  menuWidth$: Observable<number>;

  isProcessing = false;
  flexDirection$: Observable<string>;
  rightColumnHasItems$: Observable<boolean>;
  nextStepIsDisabled$: Observable<boolean>;
  assetMainContainerGap = ASSET_MAIN_CONTAINER_GAP;
  assetWidth = 200;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly imageProcessor: ImageResizingService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly assetUiService: AssetUiService,
    private readonly finishCreationFlow: FinishCreationFlowService,
  ) {}

  ngOnInit() {
    // adjust stepper position
    this.subscriptions.add(
      this.isPaidApp$.subscribe(isPaidApp => {
        if (isPaidApp) {
          this.store.dispatch(new SkipToStep(2));
        } else {
          this.store.dispatch(new SkipToStep(3));
        }
      }),
    );

    // get route parameters
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        const appId = params['appId'];
        if (appId) {
          this.fetchAssets(Number(appId));
          this.setCurrentAppId(Number(appId));
        }
      }),
    );

    // stepper disabling logic
    this.nextStepIsDisabled$ = combineLatest([
      this.fetchedAssets$,
      this.planId$,
      this.isLoading$,
    ]).pipe(
      filter(([fetchedAssets, planId]) => !!fetchedAssets && !!planId),
      map(([fetchedAssets, planId, isLoading]) => {
        const requiredAssets = ASSETS_DIMENSIONS.filter(
          dim => dim.plan.includes(planId) && dim.isRequired,
        ).map(dim => dim.name);
        const isMissingAsset = requiredAssets.some(asset => !fetchedAssets[asset]);

        return isMissingAsset || isLoading;
      }),
    );

    // adjust UI
    this.subscriptions.add(
      this.assetUiService
        .setupAssetWidthCalculation(
          this.availableOnAndroidTV$,
          this.isCreationProcess$,
          this.menuWidth$,
        )
        .subscribe(width => {
          this.assetWidth = width;
        }),
    );

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'PageCreationSettings',
        component: 'Page',
      }),
    );
    this.store.dispatch(new LogEvent('Page Viewed'));
  }

  onUploadAssetSelected(event: { source: ImageSource; selectedImage: SelectedImage }) {
    this.onRawImageSelected(event.source, event.selectedImage);
  }

  async onRawImageSelected(source: ImageSource, selectedImage: SelectedImage) {
    const planId = this.store.selectSnapshot(AppConfigurationState.currentAppIdPlan);
    if (!selectedImage) {
      return;
    }
    this.setRawImage(source, selectedImage.file);
    this.resetRawImages(source);
    this.isProcessing = true;

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Upload-${source}`,
        component: 'ImageUpload',
      }),
    );
    this.store.dispatch(new LogEvent('Source Image Uploaded'));

    try {
      const resizedImages = await this.imageProcessor.resizeImages(
        selectedImage.file,
        source,
        planId,
      );
      resizedImages.forEach((blob, name) => {
        const imageFile = new File([blob], name, { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(imageFile);
        this.storeAsset(name, imageFile, imageUrl);
      });
    } catch (error) {
      console.error('Error processing images:', error);
    } finally {
      this.postImageAssets();
      this.isProcessing = false;
    }
  }

  private resetRawImages(source: ImageSource) {
    if (source === 'rawSplashscreen') {
      this.setRawImage('splashscreen', null);
      this.setRawImage('splashscreenLarge', null);
      this.setRawImage('splashscreenTv', null);
      this.setRawImage('topShelfTv', null);
    } else {
      this.setRawImage('logo', null);
      this.setRawImage('iconTV', null);
    }
  }

  async openCropper(key: AssetKey) {
    const rawImages = this.store.selectSnapshot(AssetsState.rawImages);
    const assets = this.store.selectSnapshot(AssetsState.fetchedAssets);

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Crop-${key}`,
        component: 'Cropper',
      }),
    );
    this.store.dispatch(new LogEvent('Cropper Opened'));

    // determine the source image
    let originalFile: File | null = null;
    let sourceImageUrl: string | null = null;
    let savedState: CropperState = null;

    if (rawImages) {
      // handle logo/iconTV assets
      if (key === 'logo' || key === 'iconTv') {
        originalFile =
          rawImages[key] || (rawImages['rawLogo'] ? rawImages['rawLogo'] : null);
      } else {
        // handle splashscreen assets
        originalFile =
          rawImages[key] ||
          (rawImages['rawSplashscreen'] ? rawImages['rawSplashscreen'] : null);
      }
    }
    // set the source image URL
    if (originalFile) {
      sourceImageUrl = URL.createObjectURL(originalFile);
      savedState = this.store.selectSnapshot(AssetsState.cropperStates)?.[key];
    } else if (assets[key]) {
      sourceImageUrl = constructAssetUrl(assets[key]);
    } else {
      console.error(`No source image found for key: ${key}`);

      return;
    }
    const width = ASSETS_DIMENSIONS.find(dimension => dimension.name === key)?.width;
    const height = ASSETS_DIMENSIONS.find(dimension => dimension.name === key)?.height;
    const dialogRef = this.dialog.open(ImageCropperComponent, {
      data: {
        sourceImageUrl,
        aspectRatio: width / height,
        width,
        height,
        key,
        savedState,
      },
      disableClose: true,
      panelClass: 'cropper-dialog',
      maxWidth: '95vw',
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result) {
      const file = new File([result], key, { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(file);
      this.storeAsset(key, file, imageUrl);
      this.postImageAssets();
    }
  }

  async replaceAsset(key: string) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg';

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Replace-${key}`,
        component: 'ImageUpload',
      }),
    );
    this.store.dispatch(new LogEvent('Image Replaced'));

    input.onchange = async (event: any) => {
      const file = event.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      if (!file) {
        return;
      }
      this.setRawImage(key, file);
      this.storeAsset(key, file, imageUrl);
      this.saveCropperState(key, null);
      this.postImageAssets();
    };

    input.click();
  }

  onNextStep = () => {
    const current = this.store.selectSnapshot(StepperState.currentStep);
    const length = this.store.selectSnapshot(StepperState.stepsLength);
    // we use the "second to last" step because the last step is a dummy, display-only step
    const isSecondToLast = current === length - 2;

    if (isSecondToLast) {
      // finish the creation flow (opens publishing modal and triggers app creation)
      this.finishCreationFlow.run();
    } else {
      // if not, moves to monetization
      this.store.dispatch(new NextStep());
    }
  };

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  @Dispatch()
  fetchAssets(appId: number) {
    return new FetchAssetsRequest(appId);
  }

  @Dispatch()
  setCurrentAppId(appId: number) {
    return new SetCurrentApp(appId);
  }

  @Dispatch()
  setRawImage(key: string, image: File) {
    return new SetRawImage(key, image);
  }

  @Dispatch()
  postImageAssets() {
    return new PostImageAssetsRequest();
  }

  @Dispatch()
  storeAsset(key: string, file: File, url: string) {
    return new StoreAsset(key, file, url);
  }

  @Dispatch()
  saveCropperState(key: string, data: CropperState) {
    return new SaveCropperState(key, data);
  }
}
